import tzlookup from "tz-lookup";
import { DateTime } from "luxon";

import {
  COMMON_TIMEZONE_LABELS,
  RUSSIAN_TIMEZONE_LABELS,
} from "@/config/timezones";

/**
 * Возвращает IANA-идентификатор таймзоны по координатам
 * @param {number} lat
 * @param {number} lon
 * @returns {string}
 */
export function getTimezoneByCoords(lat, lon) {
  try {
    return tzlookup(lat, lon);
  } catch {
    return "";
  }
}

/**
 * Возвращает строку UTC-смещения для таймзоны, например "+07:00"
 * @param {string} timezone
 * @returns {string}
 */
export function getTimezoneOffset(timezone) {
  if (!timezone) return "";
  const dt = DateTime.now().setZone(timezone);
  if (!dt.isValid) return "";
  return dt.toFormat("ZZ");
}

/**
 * Возвращает русифицированную подпись таймзоны для селектов
 * @param {string} timezone
 * @returns {string}
 */
export function getTimezoneLabel(timezone) {
  if (!timezone) return "";

  const city =
    RUSSIAN_TIMEZONE_LABELS[timezone] ||
    COMMON_TIMEZONE_LABELS[timezone] ||
    timezone.split("/").pop().replace(/_/g, " ");
  const offset = getTimezoneOffset(timezone);

  return offset ? `${city} (UTC${offset}) — ${timezone}` : timezone;
}

/**
 * Возвращает список таймзон для выбора в настройках.
 * Российские зоны идут первыми, дальше — распространенные и поддерживаемые браузером IANA-зоны.
 * @returns {Array<{value: string, label: string}>}
 */
export function getTimezoneOptions() {
  const supported =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : [];
  const values = [
    ...Object.keys(RUSSIAN_TIMEZONE_LABELS),
    ...Object.keys(COMMON_TIMEZONE_LABELS),
    ...supported,
  ];

  return [...new Set(values)].map((value) => ({
    value,
    label: getTimezoneLabel(value),
  }));
}

/**
 * Пересчитывает значение datetime-local из одной таймзоны в другую,
 * сохраняя момент времени (используется при смене адреса точки)
 * @param {string} localDatetime - "YYYY-MM-DDTHH:mm"
 * @param {string} fromTz
 * @param {string} toTz
 * @returns {string|null}
 */
export function convertDatetimeBetweenTimezones(
  localDatetime,
  fromTz,
  toTz
) {
  if (!localDatetime || !fromTz || !toTz || fromTz === toTz)
    return localDatetime;
  const dt = DateTime.fromISO(localDatetime, { zone: fromTz });
  if (!dt.isValid) return localDatetime;
  return dt.setZone(toTz).toFormat("yyyy-MM-dd'T'HH:mm");
}

/**
 * Конвертирует значение datetime-local (локальное время точки) в UTC ISO для сервера
 * @param {string} localDatetime - "YYYY-MM-DDTHH:mm"
 * @param {string} timezone
 * @returns {string|null}
 */
export function toUTCIso(localDatetime, timezone) {
  if (!localDatetime || !timezone) return localDatetime ?? null;
  const dt = DateTime.fromISO(localDatetime, { zone: timezone });
  if (!dt.isValid) return localDatetime;
  return dt.toUTC().toISO();
}

/**
 * Конвертирует UTC ISO с сервера в локальное время по таймзоне для datetime-local инпута
 * @param {string} utcDatetime
 * @param {string} timezone
 * @returns {string|null}
 */
export function toLocalDatetime(utcDatetime, timezone) {
  if (!utcDatetime || !timezone) return utcDatetime ?? null;
  const dt = DateTime.fromISO(utcDatetime, { zone: "UTC" });
  if (!dt.isValid) return utcDatetime;
  return dt.setZone(timezone).toFormat("yyyy-MM-dd'T'HH:mm");
}

/**
 * Формирует planned_datetime для payload: локальное время точки → UTC ISO
 * @param {string} localDatetime
 * @param {string} timezone
 * @returns {string|null}
 */
export function buildPlannedDatetime(localDatetime, timezone) {
  return toUTCIso(localDatetime, timezone);
}

/**
 * Формирует дату-время для XML ЭДО с offset точки маршрута.
 * В отличие от buildPlannedDatetime не переводит значение в UTC, потому что
 * XSD Saby хранит локальное время операции вместе со смещением часового пояса.
 * @param {string} localDatetime
 * @param {string} timezone
 * @returns {string|null}
 */
export function buildEdoDatetimeWithOffset(localDatetime, timezone) {
  if (!localDatetime) return localDatetime ?? null;

  const stringValue = String(localDatetime).trim();
  if (/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(stringValue)) {
    return stringValue;
  }

  const dt = DateTime.fromISO(stringValue, { zone: timezone || "UTC" });
  if (!dt.isValid) return localDatetime;

  return dt.toFormat("yyyy-MM-dd'T'HH:mm:ssZZ");
}

// Определяет timezone точки маршрута.
// Приоритет: явный timezone из данных → координаты через tz-lookup → пустая строка.
export function timezoneFromPoint(point) {
  if (!point) return "";
  if (point.timezone) return point.timezone;
  const lat = Number(point.lat);
  const lon = Number(point.lon);
  if (lat && lon) return getTimezoneByCoords(lat, lon);
  return "";
}
